import {
  BuildingOffice2Icon,
  FolderIcon,
  UsersIcon,
  TagIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  EyeSlashIcon,
  ShoppingBagIcon,
  EnvelopeOpenIcon,
  ChartBarIcon,
  BookOpenIcon,
  DocumentDuplicateIcon,
  RectangleStackIcon,
  MegaphoneIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { NavigationItem } from "./types";
import { GraduationCap, Percent, TicketIcon } from "lucide-react";

/**
 * Each NavigationItem can have:
 * - name (string)
 * - href (string)
 * - icon (React component)
 * - current (boolean)
 * - internalName (string)
 * - permissions (string[])
 * - isCertainPathOnly (boolean)
 * - children?: NavigationItem[]
 */
const navigation: NavigationItem[] = [
  {
    name: "Events",
    href: "/event-registration/admin",
    icon: CalendarDaysIcon,
    current: false,
    internalName: "event-registration-admin",
    roles: ["ROLE_SUPER_ADMIN"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Management",
        href: "/event-registration/admin/events",
        icon: ClipboardDocumentListIcon,
        current: false,
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
        internalName: "event-management",
      },
      {
        name: "Categories",
        href: "/event-registration/admin/event-categories",
        icon: FolderIcon,
        current: false,
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
        internalName: "event_categories",
      },
      {
        name: "Instructors",
        href: "/event-registration/admin/event-instructors",
        icon: GraduationCap,
        current: false,
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
        internalName: "event_instructors",
      },
      {
        name: "Venues",
        href: "/event-registration/admin/venues",
        icon: MapPinIcon,
        current: false,
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
        internalName: "event_venues",
      },
      {
        name: "Vouchers",
        href: "/event-registration/admin/vouchers",
        icon: TicketIcon,
        current: false,
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
        internalName: "event_vouchers",
      },
      {
        name: "Discounts",
        href: "/event-registration/admin/discounts",
        icon: Percent,
        current: false,
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
        internalName: "event_discounts",
      },
    ],
  },

  {
    name: "Email Management",
    href: "/email-management",
    icon: EnvelopeIcon,
    current: false,
    internalName: "email_management",
    roles: ["ROLE_SUPER_ADMIN"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Email Templates",
        href: "/email-management/email-templates",
        icon: DocumentDuplicateIcon,
        current: false,
        internalName: "email_templates",
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
      },
      {
        name: "Email Campaigns",
        href: "/email-management/email-campaigns",
        icon: MegaphoneIcon,
        current: false,
        internalName: "email_campaigns",
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
      },
      {
        name: "Template Categories",
        href: "/email-management/email-template-categories",
        icon: FolderIcon,
        current: false,
        internalName: "email_template_categories",
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
      },
      {
        name: "Email Event Logs",
        href: "/email-management/email/activity",
        icon: ChartBarIcon,
        current: false,
        internalName: "email_event_logs",
        isCertainPathOnly: true,
        roles: ["ROLE_SUPER_ADMIN"],
      },
    ],
  },

  {
    name: "Resource Library",
    href: "#",
    icon: BookOpenIcon,
    current: false,
    internalName: "resource_management",
    roles: ["ROLE_SUPER_ADMIN"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Resource Management",
        href: "/admin/resources",
        icon: RectangleStackIcon,
        current: false,
        internalName: "resource_management",
        roles: ["ROLE_SUPER_ADMIN"],
        isCertainPathOnly: true,
      },
      {
        name: "File Manager",
        href: "#file-manager",
        icon: FolderIcon,
        current: false,
        internalName: "file_manager",
        roles: ["ROLE_SUPER_ADMIN"],
        isCertainPathOnly: true,
        isDialog: true,
        dialogAction: "fileManager",
      },
      {
        name: "Resource Categories",
        href: "/hub/admin/resource-categories",
        icon: FolderIcon,
        current: false,
        internalName: "resourceCategories",
        roles: ["ROLE_SUPER_ADMIN"],
      },
      {
        name: "Resource Tags",
        href: "/hub/admin/resource-tags",
        icon: TagIcon,
        current: false,
        internalName: "resourceTags",
        roles: ["ROLE_SUPER_ADMIN"],
      },
    ],
  },

  {
    name: "Company & Users",
    href: "#",
    icon: BuildingOffice2Icon,
    current: false,
    internalName: "user_management",
    roles: ["ROLE_SUPER_ADMIN"],
    children: [
      {
        name: "Company Management",
        href: "/admin/companies",
        icon: BuildingOffice2Icon,
        current: false,
        internalName: "company_management",
        roles: ["ROLE_SUPER_ADMIN"],
        isCertainPathOnly: true,
      },
      {
        name: "User Management",
        href: "/hub/users",
        icon: UsersIcon,
        current: false,
        internalName: "user_list",
        roles: ["ROLE_SUPER_ADMIN"],
      },
      {
        name: "Business Roles",
        href: "/hub/users/business-roles-permissions",
        icon: ShieldCheckIcon,
        current: false,
        internalName: "business_roles_permissions",
        roles: ["ROLE_SUPER_ADMIN"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Stochastic",
    href: "#",
    icon: ChartBarIcon,
    current: false,
    internalName: "admin",
    roles: ["ROLE_SUPER_ADMIN", "ROLE_MARKETING"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Campaign Status",
        href: "/stochastic/campaign-status",
        icon: ChartBarIcon,
        current: false,
        internalName: "stochastic_campaign_status",
        roles: ["ROLE_SUPER_ADMIN", "ROLE_MARKETING"],
      },
      {
        name: "Campaign Job Status",
        href: "/stochastic/campaign-job-status",
        icon: MegaphoneIcon,
        current: false,
        internalName: "stochastic_campaign_job_status",
        roles: ["ROLE_SUPER_ADMIN", "ROLE_MARKETING"],
      },
      {
        name: "Campaign Billing",
        href: "/stochastic/campaigns/billing",
        icon: CurrencyDollarIcon,
        current: false,
        internalName: "campaign_billing",
        roles: ["ROLE_SUPER_ADMIN", "ROLE_MARKETING"],
      },
      {
        name: "Postage Expense",
        href: "/stochastic/campaigns/postage",
        icon: EnvelopeOpenIcon,
        current: false,
        internalName: "campaignPostage",
        roles: ["ROLE_SUPER_ADMIN", "ROLE_MARKETING"],
      },
      {
        name: "Do Not Mail",
        href: "/stochastic/do-not-mail",
        icon: EyeSlashIcon,
        current: false,
        internalName: "doNotMail",
        roles: ["ROLE_SUPER_ADMIN", "ROLE_MARKETING"],
      },
      {
        name: "Manage Products",
        href: "/stochastic/products/campaign",
        icon: ShoppingBagIcon,
        current: false,
        internalName: "campaignProducts",
        roles: ["ROLE_SUPER_ADMIN", "ROLE_MARKETING"],
      },
    ],
  },
];

const adminConfig = {
  sectionName: "admin",
  defaultRoute: "/admin/companies",
  navigation,
};

export default adminConfig;
